import Button from "../../../../components/button";
import Icon from "../../../../../assets/icons";
import React from "react";
import { AssetPreviewArgsType, PreviewFileArgsType } from "./types";
import OpenLinkInNewTab from "../../link";
import { AssetUploadType, DimensionType } from "../types";
import { DEFAULT_ASSET_CONFIG } from "../constants";
import { ACCEPTED_FILE_MIME_TYPE, ACCEPTED_IMAGE_MIME_TYPE } from "../../../../utils/constants";
import { TextAlignment } from "shared/enums/TextAlignment";
import classNames from "classnames";
import AssetAccessType from "../../../../enums/AssetAccessType";
import { getToken } from "../../../../utils";
import { useDownloadApiResourceAsset } from "../dataloader";
import { useCopies } from "../../../../hooks/useCopies";
import Variant from "shared/enums/Variant";

export default function DefaultAssetPreview({ removeHandler, ...props }: AssetPreviewArgsType) {
  const removeClickHandler = React.useCallback(() => {
    removeHandler?.(props.asset.id);
  }, [removeHandler, props.asset]);

  if (!props.asset) {
    return null;
  }

  if (
    props.assetUploadType === AssetUploadType.BOTH &&
    ACCEPTED_FILE_MIME_TYPE.includes(props.asset.contentType as string)
  ) {
    const PreviewComponent = (
      <PreviewFile removeHandler={removeHandler && removeClickHandler} disabled={props.disabled} {...props.asset} />
    );
    return props.disableImagePreview ? (
      <div className="defaultAsset--fileItem">{PreviewComponent}</div>
    ) : (
      PreviewComponent
    );
  }

  return (
    <div className="defaultAsset--fileItem">
      {props.assetUploadType === AssetUploadType.FILE ||
      (props.assetUploadType === AssetUploadType.BOTH &&
        (props.disableImagePreview || props.asset.accessType === AssetAccessType.PRIVATE)) ? (
        <PreviewFile removeHandler={removeHandler && removeClickHandler} disabled={props.disabled} {...props.asset} />
      ) : (
        <PreviewImage
          url={props.asset.url}
          removeHandler={removeHandler && removeClickHandler}
          dimension={props.config?.dimension}
          disabled={props.disabled}
        />
      )}
    </div>
  );
}

export function PreviewImage({
  url,
  dimension = DEFAULT_ASSET_CONFIG.dimension,
  removeHandler,
  disabled,
  removeSize = false,
  align = TextAlignment.CENTER
}: {
  disabled?: boolean;
  url: string;
  dimension?: DimensionType;
  removeHandler?: () => void;
  align?: TextAlignment;
  removeSize?: boolean;
}) {
  return (
    <div
      className={classNames("defaultAsset--img", align)}
      style={{
        height: removeSize || dimension.maxHeight ? "auto" : dimension?.height,
        width: removeSize ? "auto" : dimension?.width
      }}>
      <img src={url} alt="" />
      {removeHandler && !disabled && (
        <Button icon={<Icon.CloseOutline />} variant={Variant.ERROR} className="closeIcon" onClick={removeHandler} />
      )}
    </div>
  );
}

export function PreviewFile(args: PreviewFileArgsType) {
  const copies = useCopies();
  const { mutateAsync: downloadAsset } = useDownloadApiResourceAsset({
    fileName: args.name || args.id,
    fileContentType: args.contentType
  });
  const iconToDisplay =
    args.contentType && ACCEPTED_IMAGE_MIME_TYPE.includes(args.contentType) ? <Icon.Image /> : <Icon.PdfFile />;

  const canDownloadPrivateFile = React.useMemo(() => args.contextId && Boolean(getToken()), [args.contextId]);

  const privateFileDownloadHandler = React.useCallback(async () => {
    if (!canDownloadPrivateFile) {
      return;
    }
    try {
      await downloadAsset({ pathParams: { id: args.id } });
    } catch (e) {}
  }, [canDownloadPrivateFile, downloadAsset, args.id]);

  return (
    <div className="defaultAsset--file">
      {args.accessType === AssetAccessType.PRIVATE ? (
        <React.Fragment>
          <span
            className={classNames("defaultAsset--fileCustom", {
              "defaultAsset--noClick": !canDownloadPrivateFile
            })}
            onClick={privateFileDownloadHandler}
            title={canDownloadPrivateFile ? args.name : copies.get("file.downloaded.not.available.msg")}>
            {iconToDisplay && iconToDisplay} {args.name && <abbr>{args.name}</abbr>}
          </span>
        </React.Fragment>
      ) : (
        <OpenLinkInNewTab url={args.url} title={args.name || ""}>
          {iconToDisplay && iconToDisplay}
          {args.name && <abbr>{args.name}</abbr>}
        </OpenLinkInNewTab>
      )}
      {args.removeHandler && !args.disabled && (
        <Button
          icon={<Icon.CloseOutline />}
          className="closeIcon"
          variant={Variant.ERROR}
          onClick={args.removeHandler}
        />
      )}
    </div>
  );
}
